import "./WebsiteBuilder.css";
import React, { useContext, useEffect, useState } from "react";
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  BgColorsOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FacebookFilled,
  GlobalOutlined,
  InfoCircleOutlined,
  InstagramOutlined,
  LinkOutlined,
  TikTokOutlined,
  WhatsAppOutlined,
  UnorderedListOutlined,
  PhoneOutlined,
  PictureOutlined,
  PlusOutlined,
  PoweroffOutlined,
  SaveOutlined,
  ShopOutlined,
  XOutlined,
  YoutubeFilled,
} from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-layout";
import {
  Button,
  Card,
  ColorPicker,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import ImageUpload from "../../../components/ImageUpload";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import {
  getBranches,
  getMyProfile,
  saveWebsiteSettings,
} from "../../../network/network";
import { handleApiError } from "../../../utils/errorHandler";

const WORKING_DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const getDefaultWorkingHours = () =>
  WORKING_DAYS.map((day) => ({
    day,
    enabled: false,
    open: "",
    close: "",
  }));

const normalizeWorkingHours = (hours) => {
  if (!Array.isArray(hours)) return getDefaultWorkingHours();

  const savedHours = new Map(hours.map((item) => [item?.day, item]));
  return getDefaultWorkingHours().map((defaultDay) => ({
    ...defaultDay,
    ...(savedHours.get(defaultDay.day) || {}),
  }));
};

const getAppConfig = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getWebsitePrimaryColor = (user) =>
  getAppConfig(user?.app_config)?.website_config?.primary_color || "";

const getSavedWebsiteSettings = (user) =>
  getAppConfig(user?.app_config)?.website_config?.settings;

const getWebsiteUrl = (user) => {
  const websiteConfig = getAppConfig(user?.app_config)?.website_config;
  return (
    user?.website_url || websiteConfig?.website_url || websiteConfig?.url || ""
  );
};

const SOCIAL_FIELDS = [
  "instagram",
  "tiktok",
  "facebook",
  "x",
  "youtube",
  "whatsapp",
  "snapchat",
  "tripadvisor",
];

const getProfilePhone = (phone) => {
  if (!phone) return "";
  const value = String(phone);
  return value.startsWith("+") ? value : `+${value}`;
};

const getDefaultSettings = (user) => ({
  enabled: false,
  primaryColor: getWebsitePrimaryColor(user) || "#bfd508",
  banners: [],
  about: {
    title: "",
    description: "",
    highlights: ["", "", ""],
    stats: [
      { value: "", label: "" },
      { value: "", label: "" },
      { value: "", label: "" },
      { value: "", label: "" },
    ],
  },
  contact: {
    phone: getProfilePhone(user?.phone),
    email: user?.email || "",
    hours: getDefaultWorkingHours(),
    address: user?.business_address || "",
  },
  social: {
    instagram: "",
    tiktok: "",
    facebook: "",
    x: "",
    youtube: "",
    whatsapp: "",
    snapchat: "",
    tripadvisor: "",
  },
  branchIds: null,
  productIds: null,
});

const loadSettings = (user) => {
  const defaults = getDefaultSettings(user);
  try {
    const serverSettings = getSavedWebsiteSettings(user);
    const parsed =
      serverSettings && typeof serverSettings === "object"
        ? serverSettings
        : {};
    const savedSettings = { ...parsed };
    delete savedSettings.categoryIds;

    return {
      ...defaults,
      ...savedSettings,
      primaryColor:
        getWebsitePrimaryColor(user) ||
        savedSettings.primaryColor ||
        defaults.primaryColor,
      about: {
        ...defaults.about,
        ...savedSettings.about,
        stats: defaults.about.stats.map((defaultStat, index) => ({
          ...defaultStat,
          ...(savedSettings.about?.stats?.[index] || {}),
        })),
      },
      contact: {
        ...defaults.contact,
        ...savedSettings.contact,
        hours: normalizeWorkingHours(savedSettings.contact?.hours),
      },
      social: SOCIAL_FIELDS.reduce(
        (social, field) => ({
          ...social,
          [field]: savedSettings.social?.[field] || "",
        }),
        {},
      ),
      banners: Array.isArray(savedSettings.banners)
        ? savedSettings.banners.slice(0, 3)
        : defaults.banners,
      branchIds:
        Array.isArray(savedSettings.branchIds) &&
        savedSettings.branchIds.length > 0
          ? savedSettings.branchIds
          : null,
      productIds: Array.isArray(savedSettings.productIds)
        ? savedSettings.productIds
        : null,
    };
  } catch {
    return defaults;
  }
};

const getBranchName = (branch, direction) => {
  if (!branch) return "";
  if (direction === "rtl") {
    return (
      branch.name_ar || branch.name_en || branch.name || branch.branch_name
    );
  }
  return branch.name_en || branch.name || branch.branch_name || branch.name_ar;
};

const getProductName = (product, direction) => {
  if (!product) return "";
  return direction === "rtl"
    ? product.name_ar || product.name || product.name_en
    : product.name_en || product.name || product.name_ar;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result));
    reader.addEventListener("error", reject);
    reader.readAsDataURL(file);
  });

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");

const getSelectedIds = (selectedIds, records) =>
  (selectedIds === null ? records.map((record) => record.id) : selectedIds)
    .filter((id) => id !== null && id !== undefined)
    .filter((id, index, ids) => ids.indexOf(id) === index);

export const buildWebsitePayload = (settings, branches, products) => ({
  is_enabled: Boolean(settings.enabled),
  primary_color: settings.primaryColor,
  banners: settings.banners.map((banner) => {
    const id = String(banner.id || "");

    return {
      ...(id && !id.startsWith("banner-") ? { id: banner.id } : {}),
      title: cleanText(banner.title),
      label: cleanText(banner.tag),
      description: cleanText(banner.description),
      background_color: banner.color || settings.primaryColor,
      image: banner.image || null,
    };
  }),
  about: {
    heading: cleanText(settings.about.title),
    description: cleanText(settings.about.description),
    highlights: settings.about.highlights.map(cleanText).filter(Boolean),
    stats: settings.about.stats
      .map((stat) => ({
        value: cleanText(stat.value),
        label: cleanText(stat.label),
      }))
      .filter((stat) => stat.value || stat.label),
  },
  contact: {
    phone_number: cleanText(settings.contact.phone),
    email: cleanText(settings.contact.email),
    address: cleanText(settings.contact.address),
    working_hours: settings.contact.hours.map((item) => ({
      day: item.day,
      is_open: Boolean(item.enabled),
      opens_at: item.enabled ? item.open || null : null,
      closes_at: item.enabled ? item.close || null : null,
    })),
  },
  social_links: SOCIAL_FIELDS.reduce((links, field) => {
    links[field] = cleanText(settings.social[field]);
    return links;
  }, {}),
  branch_ids: getSelectedIds(settings.branchIds, branches),
  product_ids: getSelectedIds(settings.productIds, products),
});

const WebsiteBuilder = () => {
  const { authUser, setAuthUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(() => loadSettings(authUser));
  const [activeTab, setActiveTab] = useState("appearance");
  const [productView, setProductView] = useState("grid");
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerColor, setBannerColor] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [bannerImageFile, setBannerImageFile] = useState(null);
  const [bannerForm] = Form.useForm();

  const direction = appConfig?.direction || "ltr";
  const websiteUrl = getWebsiteUrl(authUser);

  useEffect(() => {
    setSettings(loadSettings(authUser));
    // Reload only when the signed-in business changes, not whenever its
    // profile object receives an unrelated update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  useEffect(() => {
    let isMounted = true;
    setLoadingData(true);

    Promise.all([getBranches(), getMyProfile()])
      .then(([branchesResponse, profileResponse]) => {
        if (!isMounted) return;

        if (branchesResponse?.data?.success === false) {
          handleApiError(branchesResponse, t);
        } else {
          const branchData =
            branchesResponse?.data?.data?.branches ||
            branchesResponse?.data?.data ||
            [];
          setBranches(Array.isArray(branchData) ? branchData : []);
        }

        if (profileResponse?.data?.success === false) {
          handleApiError(profileResponse, t);
        } else {
          const user = profileResponse?.data?.data?.user;
          if (user) {
            setAuthUser(user);
            setSettings(loadSettings(user));
          }

          const categoryData = Array.isArray(user?.categories)
            ? user.categories.filter((category) =>
                Array.isArray(category?.products),
              )
            : [];
          const productData = categoryData.flatMap(
            (category) => category.products,
          );

          setProducts(productData);
        }
      })
      .catch((error) => {
        if (isMounted) handleApiError(error, t);
      })
      .finally(() => {
        if (isMounted) setLoadingData(false);
      });

    return () => {
      isMounted = false;
    };
  }, [setAuthUser, t]);

  const updateSettings = (updates) => {
    setSettings((current) => ({ ...current, ...updates }));
  };

  const updateNestedSettings = (section, updates) => {
    setSettings((current) => ({
      ...current,
      [section]: { ...current[section], ...updates },
    }));
  };

  const toggleCollectionItem = (field, id) => {
    setSettings((current) => {
      const selectedIds =
        current[field] === null
          ? branches.map((branch) => branch.id)
          : current[field];
      const isSelected = selectedIds.includes(id);
      return {
        ...current,
        [field]: isSelected
          ? selectedIds.filter((itemId) => itemId !== id)
          : [...selectedIds, id],
      };
    });
  };

  const saveWebsite = () => {
    const payload = buildWebsitePayload(settings, branches, products);

    setSavingWebsite(true);
    saveWebsiteSettings(payload)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const updatedUser = response?.data?.data?.user;
        if (updatedUser) setAuthUser(updatedUser);

        NotificationWithIcon("success", t("website_saved"));
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => setSavingWebsite(false));
  };

  const openPublishedWebsite = () => {
    window.open(`https://${websiteUrl}`, "_blank", "noopener,noreferrer");
  };

  const openBannerModal = (banner = null) => {
    setEditingBanner(banner);
    setBannerColor(banner?.color || settings.primaryColor);
    setBannerImage(banner?.image || "");
    setBannerImageFile(null);
    bannerForm.setFieldsValue(
      banner || {
        title: "",
        tag: "",
        description: "",
      },
    );
    setBannerModalOpen(true);
  };

  const saveBanner = async () => {
    try {
      const values = await bannerForm.validateFields();
      setSavingBanner(true);
      const image = bannerImageFile
        ? await fileToDataUrl(bannerImageFile)
        : bannerImage;
      const bannerValues = { ...values, color: bannerColor, image };

      if (!editingBanner && settings.banners.length >= 3) {
        setBannerModalOpen(false);
        return;
      }

      setSettings((current) => {
        if (editingBanner) {
          return {
            ...current,
            banners: current.banners.map((banner) =>
              banner.id === editingBanner.id
                ? { ...banner, ...bannerValues }
                : banner,
            ),
          };
        }

        return {
          ...current,
          banners: [
            ...current.banners,
            { ...bannerValues, id: `banner-${Date.now()}` },
          ],
        };
      });
      setBannerModalOpen(false);
    } catch (error) {
      if (error?.errorFields) return;
      NotificationWithIcon("error", t("website_image_read_error"));
    } finally {
      setSavingBanner(false);
    }
  };

  const deleteBanner = (bannerId) => {
    setSettings((current) => ({
      ...current,
      banners: current.banners.filter((banner) => banner.id !== bannerId),
    }));
  };

  const updateHighlight = (index, value) => {
    const highlights = [...settings.about.highlights];
    highlights[index] = value;
    updateNestedSettings("about", { highlights });
  };

  const addHighlight = () => {
    updateNestedSettings("about", {
      highlights: [...settings.about.highlights, ""],
    });
  };

  const removeHighlight = (index) => {
    updateNestedSettings("about", {
      highlights: settings.about.highlights.filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    });
  };

  const updateStat = (index, field, value) => {
    const stats = settings.about.stats.map((stat, statIndex) =>
      statIndex === index ? { ...stat, [field]: value } : stat,
    );
    updateNestedSettings("about", { stats });
  };

  const updateWorkingHours = (day, updates) => {
    updateNestedSettings("contact", {
      hours: settings.contact.hours.map((item) =>
        item.day === day ? { ...item, ...updates } : item,
      ),
    });
  };

  const isProductVisible = (productId) =>
    settings.productIds === null ||
    settings.productIds.some((id) => Number(id) === Number(productId));

  const toggleProductVisibility = (productId) => {
    setSettings((current) => {
      const visibleProductIds =
        current.productIds === null
          ? products.map((product) => product.id)
          : current.productIds;
      const isVisible = visibleProductIds.some(
        (id) => Number(id) === Number(productId),
      );

      return {
        ...current,
        productIds: isVisible
          ? visibleProductIds.filter((id) => Number(id) !== Number(productId))
          : [...visibleProductIds, productId],
      };
    });
  };

  const visibleProductCount = products.filter((product) =>
    isProductVisible(product.id),
  ).length;
  const areAllProductsVisible =
    products.length > 0 && visibleProductCount === products.length;

  const toggleAllProducts = () => {
    setSettings((current) => ({
      ...current,
      productIds: areAllProductsVisible
        ? []
        : products.map((product) => product.id),
    }));
  };

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <ShopOutlined /> },
    {
      path: "/my-website",
      breadcrumbName: t("website"),
      icon: <GlobalOutlined />,
    },
  ];

  const tabs = [
    {
      key: "appearance",
      label: (
        <span>
          <BgColorsOutlined /> {t("website_appearance")}
        </span>
      ),
      children: (
        <div className="website-builder__section-grid">
          <Card className="website-builder__card" title={t("website_brand")}>
            <p className="website-builder__helper">{t("website_brand_help")}</p>
            <div className="website-builder__brand-control">
              <div
                className="website-builder__brand-swatch"
                style={{ background: settings.primaryColor }}
              >
                Aa
              </div>
              <div className="website-builder__brand-copy">
                <strong>{t("website_primary_colour")}</strong>
                <span>{settings.primaryColor.toUpperCase()}</span>
              </div>
              <div className="website-builder__color-picker">
                <ColorPicker
                  showText
                  format="hex"
                  value={settings.primaryColor}
                  onChange={(_, hex) => updateSettings({ primaryColor: hex })}
                />
              </div>
            </div>
            <div className="website-builder__tip-section">
              <InfoCircleOutlined />
              <div>
                <strong>{t("website_tip_title")}</strong>
                <p>{t("website_tip_text")}</p>
              </div>
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: "banners",
      label: (
        <span>
          <PictureOutlined /> {t("website_banners")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_hero_banners")}
          extra={
            <Button
              icon={<PlusOutlined />}
              type="primary"
              disabled={settings.banners.length >= 3}
              onClick={() => openBannerModal()}
            >
              {t("website_add_banner")}
            </Button>
          }
        >
          <p className="website-builder__helper">
            {t("website_hero_banners_help")}
          </p>
          {settings.banners.length ? (
            <div className="website-builder__banner-list">
              {settings.banners.map((banner) => (
                <div className="website-builder__banner-row" key={banner.id}>
                  <div
                    className="website-builder__banner-image"
                    style={{ background: banner.color }}
                  >
                    {banner.image ? (
                      <img src={banner.image} alt="" />
                    ) : (
                      <PictureOutlined />
                    )}
                  </div>
                  <div className="website-builder__banner-copy">
                    <strong>{banner.title}</strong>
                    <span>{banner.tag || t("website_no_label")}</span>
                  </div>
                  <Space>
                    <Tooltip title={t("edit")}>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openBannerModal(banner)}
                      />
                    </Tooltip>
                    <Tooltip title={t("delete_entity")}>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => deleteBanner(banner.id)}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              description={t("website_no_banners")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    },
    {
      key: "about",
      label: (
        <span>
          <InfoCircleOutlined /> {t("website_about")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_about_section")}
        >
          <p className="website-builder__helper">{t("website_about_help")}</p>
          <Form layout="vertical">
            <Form.Item label={t("website_section_heading")}>
              <Input
                placeholder={t("website_section_heading_placeholder")}
                value={settings.about.title}
                onChange={(event) =>
                  updateNestedSettings("about", { title: event.target.value })
                }
              />
            </Form.Item>
            <Form.Item label={t("website_about_text")}>
              <Input.TextArea
                placeholder={t("website_about_text_placeholder")}
                rows={5}
                value={settings.about.description}
                onChange={(event) =>
                  updateNestedSettings("about", {
                    description: event.target.value,
                  })
                }
              />
            </Form.Item>
            <div className="website-builder__field-group">
              <div className="website-builder__field-label">
                {t("website_highlights")}
              </div>
              <p className="website-builder__field-help">
                {t("website_highlights_help")}
              </p>
              <div className="website-builder__highlight-list">
                {settings.about.highlights.map((highlight, index) => (
                  <div
                    className="website-builder__highlight-row"
                    key={`highlight-${index}`}
                  >
                    <span className="website-builder__row-number">
                      {index + 1}
                    </span>
                    <Input
                      aria-label={t("website_highlight_number", {
                        number: index + 1,
                      })}
                      placeholder={t("website_highlight_placeholder")}
                      value={highlight}
                      onChange={(event) =>
                        updateHighlight(index, event.target.value)
                      }
                    />
                    <Tooltip title={t("remove")}>
                      <Button
                        aria-label={t("remove")}
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeHighlight(index)}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
              <Button
                className="website-builder__add-row"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addHighlight}
              >
                {t("website_add_highlight")}
              </Button>
            </div>
            <div className="website-builder__field-group website-builder__stats-group">
              <div className="website-builder__field-label">
                {t("website_stats")}
              </div>
              <p className="website-builder__field-help">
                {t("website_stats_help")}
              </p>
              <div className="website-builder__stats-list">
                {settings.about.stats.map((stat, index) => (
                  <div
                    className="website-builder__stat-row"
                    key={`stat-${index}`}
                  >
                    <Input
                      aria-label={t("website_stat_value", {
                        number: index + 1,
                      })}
                      placeholder={t("website_stat_value_placeholder")}
                      value={stat.value}
                      onChange={(event) =>
                        updateStat(index, "value", event.target.value)
                      }
                    />
                    <Input
                      aria-label={t("website_stat_label", {
                        number: index + 1,
                      })}
                      placeholder={t("website_stat_label_placeholder")}
                      value={stat.label}
                      onChange={(event) =>
                        updateStat(index, "label", event.target.value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </Form>
        </Card>
      ),
    },
    {
      key: "contact",
      label: (
        <span>
          <PhoneOutlined /> {t("website_contact")}
        </span>
      ),
      children: (
        <div className="website-builder__section-grid">
          <Card
            className="website-builder__card"
            title={t("website_contact_details")}
          >
            <Form layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={t("phone_number")}>
                    <Input
                      placeholder={t("phone_number_placeholder")}
                      value={settings.contact.phone}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          phone: event.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("email")}>
                    <Input
                      placeholder={t("email_placeholder")}
                      type="email"
                      value={settings.contact.email}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          email: event.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t("address")}>
                    <Input
                      placeholder={t("address_placeholder")}
                      value={settings.contact.address}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          address: event.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <div className="website-builder__working-hours">
                <div className="website-builder__working-hours-heading">
                  <div className="website-builder__working-hours-icon">
                    <ClockCircleOutlined />
                  </div>
                  <div>
                    <strong>{t("website_working_hours")}</strong>
                    <span>{t("website_working_hours_help")}</span>
                  </div>
                </div>
                <div className="website-builder__hours-list">
                  {settings.contact.hours.map((item) => (
                    <div className="website-builder__hours-row" key={item.day}>
                      <strong className="website-builder__day">
                        {t(`website_day_${item.day}`)}
                      </strong>
                      <div className="website-builder__hours-status">
                        <Switch
                          size="small"
                          checked={item.enabled}
                          aria-label={t("website_toggle_day", {
                            day: t(`website_day_${item.day}`),
                          })}
                          onChange={(enabled) =>
                            updateWorkingHours(item.day, { enabled })
                          }
                        />
                        <span>
                          {item.enabled
                            ? t("website_open")
                            : t("website_closed")}
                        </span>
                      </div>
                      <div className="website-builder__time-range">
                        <Input
                          type="time"
                          disabled={!item.enabled}
                          value={item.open}
                          aria-label={t("website_open_time_for", {
                            day: t(`website_day_${item.day}`),
                          })}
                          onChange={(event) =>
                            updateWorkingHours(item.day, {
                              open: event.target.value,
                            })
                          }
                        />
                        <span>{t("website_to")}</span>
                        <Input
                          type="time"
                          disabled={!item.enabled}
                          value={item.close}
                          aria-label={t("website_close_time_for", {
                            day: t(`website_day_${item.day}`),
                          })}
                          onChange={(event) =>
                            updateWorkingHours(item.day, {
                              close: event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Form>
          </Card>
        </div>
      ),
    },
    {
      key: "social",
      label: (
        <span>
          <LinkOutlined /> {t("website_social")}
        </span>
      ),
      children: (
        <div className="website-builder__section-grid">
          <Card className="website-builder__card" title={t("website_social")}>
            <p className="website-builder__helper">
              {t("website_social_help")}
            </p>
            <div className="website-builder__social-list">
              {[
                {
                  key: "facebook",
                  label: "facebook",
                  value: settings.social.facebook,
                  title: <FacebookFilled />,
                  className: "is-facebook",
                  placeholder: "https://facebook.com/yourpage",
                },
                {
                  key: "x",
                  label: "x",
                  value: settings.social.x,
                  title: <XOutlined />,
                  className: "is-x",
                  placeholder: "https://x.com/yourhandle",
                },
                {
                  key: "youtube",
                  label: "youtube",
                  value: settings.social.youtube,
                  title: <YoutubeFilled />,
                  className: "is-youtube",
                  placeholder: "https://youtube.com/@yourchannel",
                },
                {
                  key: "instagram",
                  label: "instagram",
                  value: settings.social.instagram,
                  title: <InstagramOutlined />,
                  className: "is-instagram",
                  placeholder: "https://instagram.com/yourpage",
                },
                {
                  key: "tiktok",
                  label: "tiktok",
                  value: settings.social.tiktok,
                  title: <TikTokOutlined />,
                  className: "is-tiktok",
                  placeholder: "https://tiktok.com/@yourhandle",
                },
                {
                  key: "whatsapp",
                  label: "whatsapp",
                  value: settings.social.whatsapp,
                  title: <WhatsAppOutlined />,
                  className: "is-whatsapp",
                  placeholder: "https://wa.me/yourphonenumber",
                },
                {
                  key: "snapchat",
                  label: "snapchat",
                  value: settings.social.snapchat,
                  title: "S",
                  className: "is-snapchat",
                  placeholder: "https://snapchat.com/add/yourname",
                },
                {
                  key: "tripadvisor",
                  label: "tripadvisor",
                  value: settings.social.tripadvisor,
                  title: "TA",
                  className: "is-tripadvisor",
                  placeholder: "https://tripadvisor.com/yourrestaurant",
                },
              ].map((link) => (
                <div
                  className={`website-builder__social-item ${link.className}`}
                  key={link.key}
                >
                  <div
                    className={`website-builder__social-icon ${link.className}`}
                  >
                    {link.title}
                  </div>
                  <Input
                    placeholder={link.placeholder}
                    value={link.value || undefined}
                    onChange={(event) =>
                      updateNestedSettings("social", {
                        [link.key]: event.target.value,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: "branches",
      label: (
        <span>
          <EnvironmentOutlined /> {t("website_branches")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_online_branches")}
          extra={
            <Button type="link" onClick={() => navigate("/branches")}>
              {t("website_manage_branches")} <ArrowRightOutlined />
            </Button>
          }
        >
          <p className="website-builder__helper">
            {t("website_branches_help")}
          </p>
          {loadingData ? (
            <div className="website-builder__loading">
              <Spin />
            </div>
          ) : branches.length ? (
            <div className="website-builder__branch-grid">
              {branches.map((branch) => {
                const isVisible =
                  settings.branchIds === null ||
                  settings.branchIds.includes(branch.id);

                return (
                  <article
                    className={`website-builder__branch-card ${
                      isVisible ? "is-visible" : ""
                    }`}
                    key={branch.id}
                  >
                    <div className="website-builder__branch-icon">
                      <ShopOutlined />
                    </div>
                    <div className="website-builder__branch-copy">
                      <strong>{getBranchName(branch, direction)}</strong>
                      <span>
                        {branch.business_address || branch.address || "—"}
                      </span>
                    </div>
                    <div className="website-builder__visibility-control">
                      <span>
                        {isVisible ? t("website_visible") : t("website_hidden")}
                      </span>
                      <Switch
                        checked={isVisible}
                        aria-label={t("website_toggle_branch", {
                          branch: getBranchName(branch, direction),
                        })}
                        onChange={() =>
                          toggleCollectionItem("branchIds", branch.id)
                        }
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <Empty
              description={t("website_no_branches")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    },
    {
      key: "menu",
      label: (
        <span>
          <AppstoreOutlined /> {t("website_menu")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_menu_visibility")}
          extra={
            <Button type="link" onClick={() => navigate("/products")}>
              {t("website_manage_products")} <ArrowRightOutlined />
            </Button>
          }
        >
          {loadingData ? (
            <div className="website-builder__loading">
              <Spin />
            </div>
          ) : products.length ? (
            <div className="website-builder__menu-preview">
              <div className="website-builder__menu-notice">
                <div className="website-builder__menu-summary-icon">
                  <AppstoreOutlined />
                </div>
                <div>
                  <strong>{t("website_all_products")}</strong>
                  <span>{t("website_product_visibility_help")}</span>
                </div>
                <span className="website-builder__visible-count">
                  {visibleProductCount} / {products.length}
                </span>
                <Button
                  className="website-builder__bulk-product-action"
                  onClick={toggleAllProducts}
                  type="link"
                >
                  {areAllProductsVisible
                    ? t("website_hide_all_products")
                    : t("website_show_all_products")}
                </Button>
                <div className="website-builder__product-view-toggle">
                  <Tooltip title={t("website_grid_view")}>
                    <Button
                      aria-label={t("website_grid_view")}
                      className={productView === "grid" ? "is-active" : ""}
                      icon={<AppstoreOutlined />}
                      onClick={() => setProductView("grid")}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title={t("website_list_view")}>
                    <Button
                      aria-label={t("website_list_view")}
                      className={productView === "list" ? "is-active" : ""}
                      icon={<UnorderedListOutlined />}
                      onClick={() => setProductView("list")}
                      type="text"
                    />
                  </Tooltip>
                </div>
              </div>

              <div
                className={`website-builder__product-grid is-${productView}`}
              >
                {products.map((product) => {
                  const isVisible = isProductVisible(product.id);

                  return (
                    <article
                      className={`website-builder__product-card ${
                        isVisible ? "is-visible" : "is-hidden"
                      }`}
                      key={product.id}
                    >
                      <div className="website-builder__product-image">
                        <img
                          src={product.image || "/images/image-not-found.png"}
                          alt={getProductName(product, direction)}
                        />
                      </div>

                      <div className="website-builder__product-copy">
                        <strong>{getProductName(product, direction)}</strong>
                        <CurrencyFormatter value={product.price} />
                      </div>

                      <div className="website-builder__product-visibility">
                        <span>
                          {isVisible
                            ? t("website_visible")
                            : t("website_hidden")}
                        </span>
                        <Switch
                          checked={isVisible}
                          aria-label={t("website_toggle_product", {
                            product: getProductName(product, direction),
                          })}
                          onChange={() => toggleProductVisibility(product.id)}
                        />
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ) : (
            <Empty
              description={t("website_no_products")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    },
  ];

  return (
    <div className="website-builder" dir={direction}>
      <Card className="website-builder__header-card">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("website")}
          ghost={false}
          extra={[
            <Button
              key="save"
              type="primary"
              icon={<SaveOutlined />}
              loading={savingWebsite}
              disabled={loadingData}
              onClick={saveWebsite}
            >
              {t("save")}
            </Button>,
            settings.enabled && websiteUrl && (
              <Button
                key="preview"
                icon={<EyeOutlined />}
                onClick={openPublishedWebsite}
              >
                {t("preview")}
              </Button>
            ),
          ].filter(Boolean)}
        />
      </Card>

      <Card
        className={`website-builder__status ${settings.enabled ? "is-live" : "is-offline"}`}
      >
        <div className="website-builder__status-icon">
          {settings.enabled ? <CheckCircleFilled /> : <PoweroffOutlined />}
        </div>
        <div className="website-builder__status-copy">
          <strong>
            {settings.enabled ? t("website_enabled") : t("website_disabled")}
          </strong>
          {settings.enabled && websiteUrl && (
            <a
              href={`https://${websiteUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="website-builder__status-url"
            >
              {websiteUrl}
            </a>
          )}
        </div>
        <div className="website-builder__status-switch">
          <span>{settings.enabled ? t("website_on") : t("website_off")}</span>
          <Switch
            checked={settings.enabled}
            onChange={(enabled) => updateSettings({ enabled })}
          />
        </div>
      </Card>

      <div
        className="website-builder__tab-list"
        role="tablist"
        aria-label={t("website_builder_sections")}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              id={`website-builder-tab-${tab.key}`}
              type="button"
              role="tab"
              aria-controls={`website-builder-panel-${tab.key}`}
              aria-selected={isActive}
              className={`website-builder__tab ${isActive ? "is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        id={`website-builder-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`website-builder-tab-${activeTab}`}
      >
        {tabs.find((tab) => tab.key === activeTab)?.children}
      </div>

      <Modal
        rootClassName="website-builder-modal"
        title={
          editingBanner ? t("website_edit_banner") : t("website_add_banner")
        }
        open={bannerModalOpen}
        onCancel={() => setBannerModalOpen(false)}
        onOk={saveBanner}
        okText={t("save")}
        confirmLoading={savingBanner}
        destroyOnClose
      >
        <Form form={bannerForm} layout="vertical">
          <Form.Item
            name="title"
            label={t("website_banner_title")}
            rules={[
              { required: true, message: t("website_banner_title_required") },
            ]}
          >
            <Input placeholder={t("website_banner_title_placeholder")} />
          </Form.Item>
          <Form.Item name="tag" label={t("website_banner_label")}>
            <Input placeholder={t("website_banner_label_placeholder")} />
          </Form.Item>
          <Form.Item name="description" label={t("description")}>
            <Input.TextArea
              placeholder={t("website_banner_description_placeholder")}
              rows={3}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label={t("website_background_colour")}>
                <div className="website-builder__color-picker">
                  <ColorPicker
                    showText
                    format="hex"
                    value={bannerColor}
                    onChange={(_, hex) => setBannerColor(hex)}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("website_banner_image")}
                extra={t("max_upload_size_2mb_warning")}
              >
                <div className="website-builder__banner-upload">
                  <ImageUpload
                    name="banner_image"
                    image={bannerImage}
                    callbackSetImageFile={(file) => {
                      setBannerImageFile(file);
                      if (!file) setBannerImage("");
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default WebsiteBuilder;
